import * as React from 'react';

import { IRegistrationFormState } from "../reducers/reactReduxForms/index";
import { IRegistrationFormControl, ICreateMessage } from "../interfaces/forms/IRegistrationComponent";
import * as Dtos from '../dtos/Fig.dtos';
import { Form, Control, actions, Errors } from "react-redux-form";
import { mapProps } from "./reactReduxFormSettings";
import { findFieldValidationFailures } from "./formErrorHelper";
import { GenericFormGroup, TextFormInput, RegistrationGenericFormGroup, ValidationMessage, RegistrationValidationMessage } from "../components/form/index";
import {
    RegistrationFormComponent,
    IRegistrationElement,
    IRegistrationGrid,
    IRegistrationMultiFormControls,
    IRegistrationSourceDocuments,

    IRegistrationGridElement
} from "../interfaces/forms/IRegistrationComponent";
import { FontAwesomeIcons, FontAwesomeIcon } from "../constants/fontAwesomeIcons";
import classNames from "classnames";
import { GridRemove } from "../enumerations/GridRemove";
import { AlertType } from '../enumerations/AlertType';

export const RegistrationComponent = {
    RegistrationControl: "IRegistrationFormControl",
    RegistrationElement: "IRegistrationElement",
    RegistrationGrid: "IRegistrationGrid",
    RegistrationMultiFormControls: "IRegistrationMultiFormControls",
    RegistrationSourceDocuments: "IRegistrationSourceDocuments",
    RegistrationGridElement: "IRegistrationGridElement"
}

export const validationRegex = {

}


export function getFormProperty(formName: string, propertyName: string, formProperties: Dtos.RegistrationFormProperty[]): Dtos.RegistrationFormProperty | undefined {

    if (propertyName && formProperties) {
        const formProperty: Dtos.RegistrationFormProperty | undefined = formProperties.find(rfp => rfp.formName.toLocaleLowerCase() == formName.toLocaleLowerCase() && rfp.propertyName.toLocaleLowerCase() == propertyName.toLocaleLowerCase());

        return formProperty;
    }

    return undefined;
}

export function getFormPropertyLabel(formName: string, propertyName: string, formProperties: Dtos.RegistrationFormProperty[]): string {
    const formProperty: Dtos.RegistrationFormProperty | undefined = getFormProperty(formName, propertyName, formProperties)

    if (formProperty && formProperty.label) {
        return formProperty.label
    }

    return propertyName;
}

/**
 * /This function renders all the controls needed for a form
 * @param form
 * @param formName
 * @param formControls
 * @param lookups
 * @param validationFailures
 */
export function renderFormControls(
    form: any,
    formName: string,
    formComponents: { [index: string]: RegistrationFormComponent },
    lookups: Dtos.Lookup[],
    validationFailures: Dtos.ResponseError[],
    formProperties: Dtos.RegistrationFormProperty[],
    formChange: typeof actions.change,
    keyPrefix?: string,
    baseFormName?: string,
    validationPrefix?: string
) {
    if (!keyPrefix) {
        keyPrefix = "";
    }
    if (!baseFormName) {
        baseFormName = formName;
    }
    if (!validationPrefix) {
        validationPrefix = "";
    }

    let renderedFormComponents: any[] = []
    let keys = Object.keys(formComponents);
    for (let key of keys) {

        switch (formComponents[key].type) {
            case RegistrationComponent.RegistrationControl:
                renderedFormComponents.push(renderSingleControl(form, formName, key, keyPrefix, validationPrefix, formComponents[key] as IRegistrationFormControl, lookups, validationFailures, formProperties));
                break;
            case RegistrationComponent.RegistrationElement:
                let formComponent = (formComponents[key] as IRegistrationElement).component
                if (formComponent) {
                    renderedFormComponents.push(React.cloneElement(formComponent, { key: keyPrefix + key }));
                }

                break;
            case RegistrationComponent.RegistrationGrid:
                renderedFormComponents.push(renderGridControls(form, formName, key, keyPrefix, validationPrefix, formComponents[key] as IRegistrationGrid, lookups, validationFailures, formProperties, formChange, baseFormName));
                break;
            case RegistrationComponent.RegistrationMultiFormControls:
                renderedFormComponents.push(renderMultiFormControls(form, formName, key, keyPrefix, validationPrefix, formComponents[key] as IRegistrationMultiFormControls, lookups, validationFailures, formProperties, formChange, baseFormName));
                break;
            case RegistrationComponent.RegistrationSourceDocuments:
                renderedFormComponents.push(renderSourceDocumentControls(form, formName, key, keyPrefix, validationPrefix, formComponents[key] as IRegistrationSourceDocuments, lookups, validationFailures, formProperties, formChange));
                break;
        }
    }

    return renderedFormComponents;
}

/**
 * This function renders a single control to be put on a redux form
 * @param formName
 * @param key
 * @param controlOption
 * @param lookups
 * @param validationFailures
 */
export function renderSingleControl(
    form: any,
    formName: string,
    key: string,
    keyPrefix: string,
    validationPrefix: string,
    controlOption: IRegistrationFormControl,
    lookups: Dtos.Lookup[],
    validationFailures: Dtos.ResponseError[],
    formProperties: Dtos.RegistrationFormProperty[]
) {

    const root = ("." + keyPrefix);
    const parentModel = keyPrefix ? root.substr(0, root.lastIndexOf(".")) : "";

    const formProperty = getFormProperty(formName, key, formProperties);
    let validators: { [index: string]: (value: any) => boolean } = {};
    let errors: { [index: string]: string } = {};

    if (controlOption.validators) {
        Object.keys(controlOption.validators).forEach(validatorKey => {
            validators[validatorKey] = (value) => controlOption.validators![validatorKey](value, form);
        })
    }

    if (controlOption.errors) {
        Object.keys(controlOption.errors).forEach(errorKey => {
            if (controlOption.errors![errorKey] instanceof Function) {
                errors[errorKey] = (controlOption.errors![errorKey] as ICreateMessage)(`${formName}.${key}`, form[key], form, parentModel);
            } else {
                errors[errorKey] = controlOption.errors![errorKey] as string;
            }
        })
    }

    return <Control key={key}
        model={"." + keyPrefix + key}
        component={controlOption.component ? controlOption.component : RegistrationGenericFormGroup}
        controlProps={{
            inputClassName: controlOption.inputClassName,
            fieldClassName: controlOption.fieldClassName,
            labelClassName: controlOption.labelClassName,
            groupClassName: controlOption.groupClassName,
            validate: controlOption.validate ? controlOption.validate : true,
            touched: controlOption.inputTouched ? controlOption.inputTouched : false,
            label: controlOption.inputLabel ? controlOption.inputLabel : formProperty && formProperty.label ? formProperty.label : key,
            inputId: formName + "." + keyPrefix + key,
            inputName: keyPrefix + key,
            inputDisabled: controlOption.disabledFn ? controlOption.disabledFn(form, keyPrefix + key) : controlOption.inputDisabled ? controlOption.inputDisabled : false,
            inputType: controlOption.inputType ? controlOption.inputType : TextFormInput,
            inputProps: controlOption.inputProps ? controlOption.inputProps : undefined,
            disableEnter: controlOption.disableEnter ? controlOption.disableEnter : false,
            validationFailures: findFieldValidationFailures(validationPrefix + key, validationFailures),
            softErrors: controlOption.softErrors ? controlOption.softErrors : undefined,
            hardErrors: controlOption.hardErrors ? controlOption.hardErrors : undefined,
            disabled: controlOption.disabled ? controlOption.disabled : false,
            removeContainer: controlOption.removeContainer,
            validationMessageAbove: controlOption.validationMessageAbove,
        }}
        mapProps={controlOption.mapProps ? controlOption.mapProps : mapProps}
        parser={controlOption.parser ? controlOption.parser : undefined}
        validators={validators}
        changeAction={controlOption.changeAction ?
            (model, value) => {
                controlOption.changeAction!(model, value, form, parentModel);
            } :
            undefined
        }
    >
        {
            controlOption.errors ?
                <Errors
                    model={"." + keyPrefix + key}
                    component={controlOption.errorComponent ? controlOption.errorComponent : ValidationMessage}
                    messages={errors ? errors : undefined}
                    show={true}
                /> :
                null
        }
        {
            controlOption.hardErrors ?
                <Errors
                    model={"." + keyPrefix + key}
                    component={controlOption.errorComponent ? controlOption.errorComponent : ValidationMessage}
                    messages={controlOption.hardErrors ? controlOption.hardErrors : undefined}
                    show={true}
                /> :
                null
        }



    </Control>
}

export function renderGridControls(
    form: any,
    formName: string,
    arrayKey: string,
    keyPrefix: string,
    validationPrefix: string,
    gridComponent: IRegistrationGrid,
    lookups: Dtos.Lookup[],
    validationFailures: Dtos.ResponseError[],
    formProperties: Dtos.RegistrationFormProperty[],
    formChange: typeof actions.change,
    baseFormName: string
) {

    const keys = Object.keys(gridComponent.components);

    const hasRecords: boolean = form[arrayKey] && form[arrayKey].length > 0;
    const gridLabel: string = gridComponent.label ? gridComponent.label : ""

    const removeConfig: GridRemove = hasRecords ? gridComponent.removeConfig ? gridComponent.removeConfig : GridRemove.All : GridRemove.None;

    if (gridComponent.minRows &&
        gridComponent.minRows > 0 &&
        (!form[arrayKey] || form[arrayKey].length < gridComponent.minRows)) {
        const currentRows = hasRecords ? 0 : form[arrayKey].length;

        let array: any[] = [];

        if (hasRecords) {
            array = [...form[arrayKey]];
        }

        while (array.length < gridComponent.minRows) {
            if (gridComponent.addRow) {
                array.push(gridComponent.addRow(gridComponent, array.length ? array.length : 0, array));
            }
            else {
                array.push({ ...gridComponent.initialState })
            }
        }

        formChange("reduxForms." + baseFormName + "." + keyPrefix + arrayKey, array);
    }


    return <div key={formName + "-" + arrayKey}>
        {
            <table className={classNames(
                "table",
                "table-bordered",
                "mt-2",
                "mb-2",
                gridComponent.className)}>
                <thead className="thead-light">
                    <tr>
                        {
                            keys.map((key, index) => {
                                const component: RegistrationFormComponent = gridComponent.components[key]
                                const columnStyle = gridComponent.columnStyles && gridComponent.columnStyles.length > index ?
                                    gridComponent.columnStyles[index] :
                                    undefined;

                                switch (component.type) {
                                    case RegistrationComponent.RegistrationControl:
                                        return <th key={formName + "-" + arrayKey + "-" + key} style={columnStyle}>
                                            {
                                                (component as IRegistrationFormControl).inputLabel ?
                                                    (component as IRegistrationFormControl).inputLabel :
                                                    getFormPropertyLabel(gridComponent.formName, key, formProperties)
                                            }
                                        </th>
                                    case RegistrationComponent.RegistrationGridElement:
                                        if (!hasRecords) {
                                            return null;
                                        }
                                        return <th key={formName + "-" + arrayKey + "-" + key} style={columnStyle}>
                                            {
                                                (component as IRegistrationGridElement).label ?
                                                    (component as IRegistrationGridElement).label :
                                                    getFormPropertyLabel(gridComponent.formName, key, formProperties)
                                            }
                                        </th>
                                    case RegistrationComponent.RegistrationMultiFormControls:
                                        let registrationMultiFormControls: IRegistrationMultiFormControls =
                                            component as IRegistrationMultiFormControls;

                                        return <th key={formName + "-" + arrayKey + "-" + key} style={columnStyle} className={registrationMultiFormControls.labelClassName}>
                                            {
                                                registrationMultiFormControls.label ?
                                                    registrationMultiFormControls.label :
                                                    getFormPropertyLabel(gridComponent.formName, key, formProperties)
                                            }
                                        </th>
                                    default:
                                        return <th key={formName + "-" + arrayKey + "-" + key} style={columnStyle}>
                                            {
                                                getFormPropertyLabel(gridComponent.formName, key, formProperties)
                                            }
                                        </th>

                                }
                            })
                        }
                        {
                            removeConfig != GridRemove.None ?
                                <th style={{ width: "80px" }}>
                                    Remove
                                    </th> :
                                null
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        hasRecords ?
                            form[arrayKey].map((record, index, recordArray) => {
                                return <tr key={formName + "-" + arrayKey + "-" + index}>
                                    {
                                        keys.map((key) => {
                                            let formComponent: any = { ...gridComponent.components[key] };

                                            formComponent.removeContainer = true;

                                            if (formComponent.type == RegistrationComponent.RegistrationGridElement) {
                                                return <td key={formName + "-" + arrayKey + "-" + index + "-" + key + "-td"}>
                                                    {formComponent.render(form, formChange, "reduxForms." + formName, lookups, record, index, recordArray, gridComponent)}
                                                </td>
                                            }

                                            return <td key={formName + "-" + arrayKey + "-" + index + "-" + key + "-td"}>
                                                {
                                                    renderFormControls(form[arrayKey][index],
                                                        gridComponent.formName,
                                                        { [key]: formComponent },
                                                        lookups,
                                                        validationFailures,
                                                        formProperties,
                                                        formChange,
                                                        arrayKey + "[" + index + "].",
                                                        baseFormName,
                                                        arrayKey + "[" + form[arrayKey][index].id + "].")
                                                }
                                            </td>
                                        })
                                    }
                                    {
                                        removeConfig != GridRemove.None ?
                                            <td style={{ verticalAlign: "middle" }}>
                                                {
                                                    (!gridComponent.minRows || recordArray.length > gridComponent.minRows) &&
                                                        (removeConfig == GridRemove.All ||
                                                            (index == 0 && removeConfig == GridRemove.First) ||
                                                            (index == recordArray.length - 1 && removeConfig == GridRemove.Last)) ?
                                                        <div className={classNames("text-center text-danger", { "disabled": gridComponent.disabled })}
                                                            onClick={() => {
                                                                if (!gridComponent.disabled) {
                                                                    let array: any[] = [];

                                                                    if (hasRecords) {
                                                                        array = [...form[arrayKey]];
                                                                    }

                                                                    array.splice(index, 1);

                                                                    formChange("reduxForms." + baseFormName + "." + keyPrefix + arrayKey, array);
                                                                }
                                                            }}>
                                                            <FontAwesomeIcon icon={FontAwesomeIcons.Regular.TIMES} fixedWidth />
                                                        </div> :
                                                        null
                                                }
                                            </td> :
                                            null
                                    }

                                </tr>

                            }) :
                            null
                    }
                    {
                        !gridComponent.maxRows ||
                            (form[arrayKey] && form[arrayKey].length < gridComponent.maxRows) ?
                            <tr>
                                <td colSpan={keys.length + 1} className="text-right">
                                    <div
                                        className={classNames("btn btn-primary", { "disabled": gridComponent.disabled })}
                                        onClick={() => {
                                            if (!gridComponent.disabled) {
                                                let array: any[] = [];

                                                if (hasRecords) {
                                                    array = [...form[arrayKey]];
                                                }

                                                if (!gridComponent.maxRows || array.length < gridComponent.maxRows) {

                                                    if (gridComponent.addRow) {
                                                        array.push(gridComponent.addRow(gridComponent, array.length ? array.length : 0, array));
                                                    }
                                                    else {
                                                        array.push({ ...gridComponent.initialState })
                                                    }

                                                    formChange("reduxForms." + baseFormName + "." + keyPrefix + arrayKey, array);
                                                }

                                                formChange("reduxForms." + baseFormName + "." + keyPrefix + arrayKey, array);
                                            }
                                        }}
                                    >
                                        Add
                                        </div>
                                </td>
                            </tr> :
                            null
                    }
                </tbody>
            </table>
        }
    </div>

}

export function renderMultiFormControls(
    form: any,
    formName: string,
    key: string,
    keyPrefix: string,
    validationPrefix: string,
    controlOption: IRegistrationMultiFormControls,
    lookups: Dtos.Lookup[],
    validationFailures: Dtos.ResponseError[],
    formProperties: Dtos.RegistrationFormProperty[],
    formChange: typeof actions.change,
    baseFormName?: string
) {

    if (!baseFormName) {
        baseFormName = formName;
    }

    return <controlOption.component key={key}
        label={controlOption.label}
        fieldClassName={controlOption.fieldClassName}
        groupClassName={controlOption.groupClassName}
        labelClassName={controlOption.labelClassName}
        removeContainer={controlOption.removeContainer}
    >
        {
            renderFormControls(form, formName, controlOption.components, lookups, validationFailures, formProperties, formChange, keyPrefix, baseFormName, validationPrefix)
        }
    </controlOption.component>

}

export function renderSourceDocumentControls(
    form: any,
    formName: string,
    key: string,
    keyPrefix: string,
    validationPrefix: string,
    sourceDocuments: IRegistrationSourceDocuments,
    lookups: Dtos.Lookup[],
    validationFailures: Dtos.ResponseError[],
    formProperties: Dtos.RegistrationFormProperty[],
    formChange: typeof actions.change
) {

    const keys = Object.keys(sourceDocuments.components);

    return <div key={key}>
        {
            sourceDocuments.label ? sourceDocuments.label : "Source Documents"
        }
        {
            renderGridControls(form, formName, key, keyPrefix, validationPrefix, sourceDocuments.components[key] as IRegistrationGrid, lookups, validationFailures, formProperties, formChange, formName)
        }
    </div>
}

//export const validateByRegex = function (pattern: RegExp, value: string) {
//    if (value) {
//        if (pattern.test(value) === true) {
//            return true;
//        }

//        return false;
//    }

//    return true;
//}

export function toFixedWithoutRounding(num: any, fixed: any) {

    if (!num) {
        return undefined;
    }

    if (!fixed) {
        return num;
    }


    var re = new RegExp('^-?\\d+(?:\.\\d{0,' + (fixed || -1) + '})?');
    return num.toString().match(re)[0];
}